import React, { useState, useEffect } from 'react'
import {
  deleteFaq    as deleteFaqAPI,
  updateFaq    as updateFaqAPI,
  useFaq       as recordUsageAPI,
  pinFaq       as pinFaqAPI,
  unpinFaq     as unpinFaqAPI,
  createFeedback  as createFeedbackAPI,
  deleteFeedback  as deleteFeedbackAPI,
  fetchFaqDetail  as fetchFaqDetailAPI,
} from '../utils/api.js'
import Feedback from './Feedback'

export default function FAQCard({ faq, currentUser, onFaqDeleted, onFaqUpdated }) {
  const [showDetail, setShowDetail]               = useState(false)
  const [copied, setCopied]                       = useState(false)
  const [isEditing, setIsEditing]                 = useState(false)
  const [draftQ, setDraftQ]                       = useState(faq.question)
  const [draftA, setDraftA]                       = useState(faq.answer)
  const [vote, setVote]                           = useState(faq.my_vote ?? null)
  const [voteId, setVoteId]                       = useState(faq.my_vote_id ?? null)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)

  const [pinned, setPinned] = useState(!!faq.pinned)
  const [pinId, setPinId]   = useState(faq.pin_id || null)

  useEffect(() => {
    setVote(faq.my_vote ?? null)
  }, [faq.my_vote])

  useEffect(() => {
    setPinned(!!faq.pinned)
    setPinId(faq.pin_id || null)
  }, [faq.pinned, faq.pin_id])

  const isOwner   = faq.user_id === currentUser.id
  const isAdmin   = currentUser.role === 'admin'
  const canEdit   = isOwner || isAdmin
  const canDelete = isOwner
  const canVote   = !isOwner && !isAdmin
  const canPin    = !isAdmin

  // copy & record usage
  const handleCopy = () => {
    navigator.clipboard.writeText(faq.answer)
      .then(async () => {
        setCopied(true)
        try {
          const updated = await recordUsageAPI(faq.id)
          onFaqUpdated(updated)
        } catch (err) {
          console.error('Failed to record usage', faq.id, err)
        }
        setTimeout(() => setCopied(false), 1500)
      })
      .catch(err => console.error('Clipboard write failed', err))
  }

  // PIN / UNPIN
  const togglePin = async () => {
    try {
      if (!pinned) {
        const newFaq = await pinFaqAPI(faq.id)
        onFaqUpdated(newFaq)
      } else {
        await unpinFaqAPI(pinId)
        onFaqUpdated({ ...faq, pinned: false, pin_id: null })
      }
    } catch (err) {
      console.error('Toggle pin failed', faq.id, err)
      alert('Could not update pin state.')
    }
  }

  // DELETE
  const handleDelete = async () => {
    if (!window.confirm('Delete this FAQ?')) return
    try {
      await deleteFaqAPI(faq.id)
      onFaqDeleted(faq.id)
    } catch (err) {
      console.error(err)
      alert('Error deleting FAQ.')
    }
  }

  // SAVE edits
  const handleSave = async () => {
    try {
      const updated = await updateFaqAPI(faq.id, {
        question: draftQ,
        answer:   draftA,
      })
      onFaqUpdated(updated)
      setIsEditing(false)
    } catch (err) {
      console.error(err)
      alert('Failed to save changes')
    }
  }

  // UP-vote
  const voteUp = async () => {
    try {
      if (vote === 1) {
        await deleteFeedbackAPI(faq.id, voteId)
      } else {
        const created = await createFeedbackAPI(faq.id, { rating: 1 })
        setVote(1)
        setVoteId(created.id)
      }
      const updatedFaq = await fetchFaqDetailAPI(faq.id)
      setVote(updatedFaq.my_vote)
      setVoteId(updatedFaq.my_vote_id)
      onFaqUpdated(updatedFaq)
    } catch (err) {
      console.error(err)
      alert('Failed to submit vote')
    }
  }

  // DOWN-vote
  const voteDown = async () => {
    try {
      if (vote === 0) {
        await deleteFeedbackAPI(faq.id, voteId)
      } else {
        const created = await createFeedbackAPI(faq.id, { rating: 0 })
        setVote(0)
        setVoteId(created.id)
      }
      const updatedFaq = await fetchFaqDetailAPI(faq.id)
      setVote(updatedFaq.my_vote)
      setVoteId(updatedFaq.my_vote_id)
      onFaqUpdated(updatedFaq)
    } catch (err) {
      console.error(err)
      alert('Failed to submit vote')
    }
  }

  return (
    <>
      <div
        className="faq-card card mb-3"
        style={{ cursor: 'pointer' }}
        onClick={() => {
          // only open when we’re not in edit mode
          if (!isEditing) setShowDetail(true);
        }}
      >
        <div className="faq-card_body card-body position-relative">
          {isEditing ? (
            <div className="faq-card__edit">
              <input
                className="faq-card__edit-input form-control mb-2"
                value={draftQ}
                onChange={e => setDraftQ(e.target.value)}
              />
              <textarea
                className="faq-card__edit-text form-control mb-2"
                rows={3}
                value={draftA}
                onChange={e => setDraftA(e.target.value)}
              />
              <div className="faq-card__edit-actions d-flex">
                <button
                  onClick={handleSave}
                  className="btn btn-sm btn-success me-2"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false)
                    setDraftQ(faq.question)
                    setDraftA(faq.answer)
                  }}
                  className="btn btn-sm btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <h5 className="faq-card__title card-title">{faq.question}</h5>
              <p className="faq-card__text card-text">{faq.answer}</p>

              <div className="faq-card__actions d-flex align-items-center">
                <button
                  onClick={e => {e.stopPropagation(); handleCopy();}}
                  className="btn btn-sm btn-outline-primary me-2"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>

                {canPin && (
                  <button
                    onClick={e => {e.stopPropagation(); togglePin();}}
                    className={`btn btn-sm me-2 ${
                      pinned ? 'btn-outline-info' : 'btn-outline-secondary'
                    }`}
                  >
                    {pinned ? 'Unpin' : 'Pin'}
                  </button>
                )}

                <button
                  onClick={e => {e.stopPropagation(); setShowFeedbackModal(true);}}
                  className="btn btn-sm btn-outline-secondary me-2"
                >
                  💬 Feedback
                </button>

                {canEdit && (
                  <button
                    onClick={e => {e.stopPropagation(); setIsEditing(true);}}
                    className="btn btn-sm btn-outline-warning me-2"
                  >
                    Edit
                  </button>
                )}

                {canDelete && (
                  <button
                    onClick={e => {e.stopPropagation(); handleDelete();}}
                    className="btn btn-sm btn-outline-danger ms-auto"
                  >
                    Delete
                  </button>
                )}
              </div>

              {canVote && (
                <div
                  className="faq-card__votes position-absolute"
                  style={{
                    bottom: '1rem',
                    right: '1rem',
                    display: 'flex',
                    gap: '0.5rem',
                  }}
                >
                  <button
                    onClick={e => {e.stopPropagation(); voteUp();}}
                    className={
                      vote === 1
                        ? 'btn btn-sm btn-success'
                        : 'btn btn-sm btn-outline-success'
                    }
                  >
                    👍
                  </button>
                  <button
                    onClick={e => {e.stopPropagation(); voteDown();}}
                    className={
                      vote === 0
                        ? 'btn btn-sm btn-danger'
                        : 'btn btn-sm btn-outline-danger'
                    }
                  >
                    👎
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Detail‐modal */}
      {showDetail && (
        <div className="faq-modal">
          <div
            className="faq-modal-backdrop"
            onClick={() => setShowDetail(false)}
          />
          <div className="faq-modal-content">
            <header className="faq-modal_header d-flex justify-content-between align-items-center mb-3">
              <h5 className="faq-modal_title mb-0">{faq.question}</h5>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowDetail(false)}
              >
                ✕
              </button>
            </header>
            <div className="faq-modal-body">
              {faq.answer.split('\n').map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      <Feedback
        faqId={faq.id}
        show={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onFeedback={() => onFaqUpdated(faq)}
      />
    </>
  )
}
